<script lang="ts">
	import type { ComponentProps } from 'svelte';
	import { HeniEditionsLogo } from '../../../assets/logos/HeniEditionsLogo';
	import { HeniPrimaryLogo } from '../../../assets/logos/HeniPrimaryLogo';
	import { getImageUrl } from '../../../utils/getImageUrl';
	import { Hero, type HeroButtonProps } from '../../Hero';
	import type { Hero as HeroType } from '../types';

	interface Props {
		button1Props?: Partial<HeroButtonProps>;
		button2Props?: Partial<HeroButtonProps>;
		class?: string;
		alt: string | null | undefined;
		hero: HeroType;
		align?: 'center' | 'left';
		showPlatformLogo?: boolean;
		dataCy: string;
		classes?: { mobileImage?: string; image?: string };
		isLive?: boolean;
		onclick?: (e?: Event) => void;
		enforcedDesktopImage?: string | null;
		enforcedMobileImage?: string | null;
		desktopImageStyle?: string;
		mobileImageStyle?: string;
		imageBreakpoint?: ComponentProps<typeof Hero>['imageBreakpoint'];
	}

	let {
		alt,
		hero,
		align = 'center',
		showPlatformLogo = false,
		dataCy,
		classes = {},
		isLive = false,
		enforcedDesktopImage,
		enforcedMobileImage,
		desktopImageStyle,
		mobileImageStyle,
		imageBreakpoint,
		onclick,
		...rest
	}: Props = $props();

	let textShadow = $derived(!!hero.text_shadow);

	const getPlatformLogoName = (link: string | null | undefined) => {
		switch (true) {
			case link?.includes('/editions'):
				return 'HeniEditionsLogo';
			case link?.includes('/primary'):
				return 'HeniPrimaryLogo';
			default:
				return '';
		}
	};

	let mobileImage = $derived(
		enforcedMobileImage || getImageUrl(hero?.['mobile_image']?.filename_disk, '2048px')
	);

	let desktopImage = $derived(
		enforcedDesktopImage || getImageUrl(hero?.desktop_image?.filename_disk, '2048px')
	);

	let description = $derived(hero?.description);
	let link = $derived(hero?.button_1_link);
	let label = $derived(hero?.button_1_label);
	let buttonProps = $derived(
		link && label
			? {
					href: link,
					label,
					arrow: true,
					variant: (hero?.button_1_variant || 'ghost') as HeroButtonProps['variant']
				}
			: undefined
	);

	let secondLink = $derived(hero?.button_2_link);
	let secondLabel = $derived(hero?.button_2_label);
	let secondaryButtonProps = $derived(
		secondLink && secondLabel
			? {
					href: secondLink,
					label: secondLabel,
					arrow: true,
					variant: (hero?.button_2_variant || 'ghost') as HeroButtonProps['variant']
				}
			: undefined
	);

	let platformLogoName = $derived(getPlatformLogoName(link));
	let secondPlatformLogoName = $derived(getPlatformLogoName(secondLink));

	let PlatformLogo = $derived(
		(() => {
			if (!showPlatformLogo) return undefined;

			if (
				platformLogoName &&
				secondPlatformLogoName &&
				platformLogoName !== secondPlatformLogoName
			) {
				return undefined;
			}

			switch (true) {
				case link?.startsWith('/editions'):
					return HeniEditionsLogo;
				case link?.startsWith('/primary'):
					return HeniPrimaryLogo;
				default:
					return undefined;
			}
		})()
	);
</script>

<Hero
	{dataCy}
	{alt}
	{textShadow}
	layerOpacity={hero?.layer_opacity}
	mobileSrc={mobileImage}
	src={desktopImage}
	title={hero?.surtitle || ''}
	{description}
	subtitle={hero?.title}
	overline={hero?.subtitle}
	{buttonProps}
	{secondaryButtonProps}
	{PlatformLogo}
	{align}
	{classes}
	class={rest.class}
	{isLive}
	{onclick}
	{desktopImageStyle}
	{mobileImageStyle}
	{imageBreakpoint}
	highResolution={!hero?.image_compressed}
/>
